import {
  SOURCE_SMS,
  SOURCE_RCS,
  SOURCE_TCAPI,
  type LocationSources,
  SOURCE_ADMIN_SMS,
} from '@rsos/constants/locationSources';
import { TEXT } from '@rsos/constants/locationTypes';
import { RCS_ACCEPT_STATUS_ACCEPTED } from '@rsos/constants/rcsStatuses';
import useHasAccessToGoogleRCS from '@rsos/hooks/useHasAccessToGoogleRCS';
import {
  ChatComponentType,
  type KeyOfChatComponentType,
} from '../../JurisdictionView/useChatComponents/typings';
import { PhoneDetails, TextMessage } from './EmergencyCall.styles';

interface TextMessageStatusProps {
  isTT911?: boolean;
  source?: LocationSources;
  classOfServiceCategory?: string;
  unreadTF911Message?: boolean;
  unreadRcsMessage?: boolean;
  isRcsAvailable?: boolean;
  rcsAcceptStatus?: number;
  isUnread?: boolean;
  isUnseen?: boolean;
  tt911Message?: string;
  rcsStatusText?: string;
  hasAgent511Access?: boolean;
  isOutboundSMS?: boolean;
  primaryChatKey?: KeyOfChatComponentType;
}

const TextMessageStatus = ({
  isTT911,
  source,
  classOfServiceCategory,
  unreadTF911Message,
  unreadRcsMessage,
  isRcsAvailable,
  rcsAcceptStatus,
  isUnread,
  isUnseen,
  tt911Message,
  rcsStatusText,
  hasAgent511Access,
  isOutboundSMS,
  primaryChatKey,
}: TextMessageStatusProps) => {
  const hasGoogleRCSAccess = useHasAccessToGoogleRCS();

  const validSources = new Set([
    SOURCE_RCS,
    SOURCE_SMS,
    SOURCE_TCAPI,
    SOURCE_ADMIN_SMS,
  ]);

  const isTextCOS = classOfServiceCategory === TEXT;

  const isMessageSourceValid =
    (source && validSources.has(source)) ||
    isTT911 ||
    isTextCOS ||
    isOutboundSMS;

  const isRCSMessageShowable =
    !unreadRcsMessage ||
    isRcsAvailable ||
    (unreadRcsMessage && rcsAcceptStatus !== RCS_ACCEPT_STATUS_ACCEPTED);

  const canShowUnreadMessage = !unreadTF911Message && isRCSMessageShowable;
  const isOutboundShowable = !unreadTF911Message && isOutboundSMS;
  const isMessageNotSeen = !(isUnread || isUnseen);
  const shouldShowAgentMessage = hasAgent511Access && tt911Message;
  const shouldShowRCSStatus = source === SOURCE_RCS && hasGoogleRCSAccess;

  const shouldShowAnything =
    isMessageSourceValid && canShowUnreadMessage && isMessageNotSeen;

  if (!shouldShowAnything) {
    return null;
  }

  const isPrimaryConversations =
    primaryChatKey === ChatComponentType.conversations;
  const isPrimaryTF911 = primaryChatKey === ChatComponentType.tf911;
  const isPrimaryRCS = primaryChatKey === ChatComponentType.rcs;

  // Agent message is ungated — highest priority regardless of bucket
  if (shouldShowAgentMessage) {
    return <TextMessage>{tt911Message}</TextMessage>;
  }

  // RCS status: requires both the existing check AND rcs being primary bucket
  if (shouldShowRCSStatus && isPrimaryRCS) {
    return <PhoneDetails>{rcsStatusText}</PhoneDetails>;
  }

  // Admin SMS is a conversations subtype — only show when conversations is primary
  if (source === SOURCE_ADMIN_SMS && isPrimaryConversations) {
    return <PhoneDetails>ADMIN LINE TEXT</PhoneDetails>;
  }

  // Outbound 911 text belongs to TF911 — only show when TF911 is primary
  if (isOutboundShowable && isPrimaryTF911) {
    return <TextMessage>OUTBOUND 911 TEXT</TextMessage>;
  }

  return <PhoneDetails>Text-to-911</PhoneDetails>;
};

export default TextMessageStatus;
